import {
  type BillingProfile,
  type BillingProfileInput,
  billingProfileSchema,
  IDENTITY_ERROR_CODES,
  type SaveBillingProfileResponse,
  saveBillingProfileResponseSchema,
} from '@noconflict/contracts';
import { z } from 'zod';
import { ApiRequestError, apiFetch } from '../../shared/api/http';
import { identityErrorMessage } from '../../shared/api/identity-error';
import type { Supabase } from '../../shared/api/supabase';
import { i18n } from '../../shared/i18n';

/** The org's billing profile, or null when none was saved yet. RLS: `billing.view`. */
export async function getBillingProfile(
  client: Supabase,
  organizationId: string,
): Promise<BillingProfile | null> {
  const { data, error } = await client
    .from('org_billing_profiles')
    .select('*')
    .eq('organization_id', organizationId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data ? billingProfileSchema.parse(data) : null;
}

/**
 * Whether the org's billing details are complete enough to order: yes or no, asked by anyone who can order (the
 * profile itself needs `billing.view`). `order_submit` checks the same thing and is the real guard.
 */
export async function getBillingProfileComplete(
  client: Supabase,
  organizationId: string,
): Promise<boolean> {
  const { data, error } = await client.rpc('billing_profile_complete', {
    p_organization_id: organizationId,
  });
  if (error) throw new Error(error.message);
  return z.boolean().parse(data);
}

/** Saved through the worker, which also keeps the payment provider's customer record in step. */
export function saveBillingProfile(
  token: string,
  organizationId: string,
  input: BillingProfileInput,
): Promise<SaveBillingProfileResponse> {
  return apiFetch(`/api/orgs/${organizationId}/billing-profile`, saveBillingProfileResponseSchema, {
    method: 'PUT',
    token,
    body: JSON.stringify(input),
  });
}

/** True when the save reached the database and only the payment provider sync failed (retried before payment). */
export function isSavedButNotSynced(error: unknown): boolean {
  return error instanceof ApiRequestError && error.code === 'billing_profile_sync_failed';
}

const identityCodes = new Set<string>(IDENTITY_ERROR_CODES);

/** Catalogue text for a failed save: the worker passes the RPC's refusal code through. */
export function billingProfileErrorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) {
    if (identityCodes.has(error.code)) return identityErrorMessage(new Error(error.code));
    return error.message;
  }
  return i18n.t('errors:identity.generic');
}
